import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { Title } from "react-admin";
import { deleteJson, getJson, postJson, putJson } from "../../utils/api";
import {
  Curriculum,
  CurriculumUnit,
  FeedbackGroup,
  LessonListItem,
} from "../../models";
import { FeedbackGroupsPanel } from "./FeedbackGroupsPanel";

const ADMIN = "/admin";
const SLUG_RE = /[^a-z0-9-]/g;

export const CurriculumDetail: React.FC = () => {
  const { curriculumId } = useParams<{ curriculumId: string }>();
  const navigate = useNavigate();
  const [curriculum, setCurriculum] = useState<Curriculum | null>(null);
  const [units, setUnits] = useState<CurriculumUnit[]>([]);
  const [feedbackGroups, setFeedbackGroups] = useState<FeedbackGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedUnit, setExpandedUnit] = useState<string | null>(null);
  const [unitLessons, setUnitLessons] = useState<
    Record<string, LessonListItem[]>
  >({});

  // Create unit dialog
  const [createUnitOpen, setCreateUnitOpen] = useState(false);
  const [newUnitId, setNewUnitId] = useState("");
  const [newUnitTitle, setNewUnitTitle] = useState("");

  // Create lesson dialog
  const [createLessonOpen, setCreateLessonOpen] = useState(false);
  const [createLessonUnitId, setCreateLessonUnitId] = useState("");
  const [newLessonTitle, setNewLessonTitle] = useState("");
  const [newLessonType, setNewLessonType] = useState("STANDARD");

  // Clone dialog
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneLessonId, setCloneLessonId] = useState("");
  const [cloneSuffix, setCloneSuffix] = useState(" (Copy)");

  // Editing title
  const [editingTitle, setEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState("");

  const fetchCurriculum = useCallback(async () => {
    if (!curriculumId) return;
    try {
      setLoading(true);
      const [curResp, unitsResp, fgResp] = await Promise.all([
        getJson(`${ADMIN}/curricula`),
        getJson(`${ADMIN}/curricula/${curriculumId}/units`),
        getJson(`${ADMIN}/curricula/${curriculumId}/feedback-groups`),
      ]);
      const { data: allCur } = await curResp.json();
      const cur = allCur?.find((c: Curriculum) => c.id === curriculumId);
      setCurriculum(cur ?? null);
      setEditTitle(cur?.title ?? "");

      const { data: unitsData } = await unitsResp.json();
      setUnits(unitsData ?? []);

      const { data: fgData } = await fgResp.json();
      setFeedbackGroups(fgData ?? []);
    } catch (e) {
      console.error("Failed to fetch curriculum", e);
    } finally {
      setLoading(false);
    }
  }, [curriculumId]);

  useEffect(() => {
    fetchCurriculum();
  }, [fetchCurriculum]);

  const fetchLessons = useCallback(
    async (unitId: string) => {
      try {
        const resp = await getJson(
          `${ADMIN}/curricula/${curriculumId}/units/${unitId}/lessons`
        );
        const { data } = await resp.json();
        setUnitLessons((prev) => ({ ...prev, [unitId]: data ?? [] }));
      } catch (e) {
        console.error("Failed to fetch lessons", e);
      }
    },
    [curriculumId]
  );

  const handleToggleUnit = (unitId: string) => {
    if (expandedUnit === unitId) {
      setExpandedUnit(null);
    } else {
      setExpandedUnit(unitId);
      if (!unitLessons[unitId]) {
        fetchLessons(unitId);
      }
    }
  };

  const handleSaveTitle = async () => {
    if (!curriculumId || !editTitle) return;
    try {
      await putJson(`${ADMIN}/curricula/${curriculumId}`, { title: editTitle });
      setEditingTitle(false);
      await fetchCurriculum();
    } catch (e) {
      console.error("Failed to update title", e);
    }
  };

  const handleToggleActive = async () => {
    if (!curriculum) return;
    try {
      await putJson(`${ADMIN}/curricula/${curriculumId}`, {
        is_active: !curriculum.is_active,
      });
      await fetchCurriculum();
    } catch (e) {
      console.error("Failed to toggle active", e);
    }
  };

  const handleCreateUnit = async () => {
    if (!newUnitId || !newUnitTitle) return;
    try {
      await postJson(`${ADMIN}/curricula/${curriculumId}/units`, {
        id: newUnitId,
        title: newUnitTitle,
      });
      setCreateUnitOpen(false);
      setNewUnitId("");
      setNewUnitTitle("");
      await fetchCurriculum();
    } catch (e: any) {
      alert(e?.error?.detail ?? "Failed to create unit");
    }
  };

  const handleArchiveUnit = async (unitId: string) => {
    if (!window.confirm(`Archive unit "${unitId}" and all its lessons?`))
      return;
    try {
      await deleteJson(`${ADMIN}/curricula/${curriculumId}/units/${unitId}`);
      await fetchCurriculum();
    } catch (e) {
      console.error("Failed to archive unit", e);
    }
  };

  const handleCreateLesson = () => {
    if (!newLessonTitle) return;
    setCreateLessonOpen(false);
    navigate("/lessons/new/edit", {
      state: {
        title: newLessonTitle,
        type: newLessonType,
        unitId: createLessonUnitId,
        curriculumId,
      },
    });
    setNewLessonTitle("");
    setNewLessonType("STANDARD");
  };

  const handleArchiveLesson = async (lessonId: string, unitId: string) => {
    if (!window.confirm("Archive this lesson?")) return;
    try {
      await deleteJson(`${ADMIN}/lessons/${lessonId}`);
      await fetchLessons(unitId);
      await fetchCurriculum();
    } catch (e) {
      console.error("Failed to archive lesson", e);
    }
  };

  const handleToggleLessonStatus = async (
    lesson: LessonListItem,
    unitId: string
  ) => {
    const action = lesson.status === "ACTIVE" ? "disable" : "enable";
    try {
      await postJson(`${ADMIN}/lessons/${lesson.id}/${action}`, {});
      await fetchLessons(unitId);
    } catch (e) {
      console.error("Failed to toggle lesson status", e);
    }
  };

  const handleCloneLesson = async () => {
    if (!cloneLessonId) return;
    try {
      const resp = await postJson(`${ADMIN}/lessons/${cloneLessonId}/clone`, {
        new_title_suffix: cloneSuffix,
      });
      const { data } = await resp.json();
      setCloneOpen(false);
      setCloneSuffix(" (Copy)");
      // Navigate to the cloned lesson editor
      navigate(`/lessons/${data.id}/edit`);
    } catch (e: any) {
      alert(e?.error?.detail ?? "Failed to clone lesson");
    }
  };

  const handleMoveUnit = async (unitId: string, direction: "up" | "down") => {
    const idx = units.findIndex((u) => u.id === unitId);
    if (idx < 0) return;
    if (direction === "up" && idx === 0) return;
    if (direction === "down" && idx === units.length - 1) return;

    const afterId =
      direction === "up"
        ? idx === 1
          ? null // move to start
          : units[idx - 2].id
        : units[idx + 1].id;

    try {
      await putJson(`${ADMIN}/curricula/${curriculumId}/units/reorder`, {
        item_id: unitId,
        after_id: afterId,
      });
      await fetchCurriculum();
    } catch (e) {
      console.error("Failed to reorder unit", e);
    }
  };

  const handleMoveLesson = async (
    lessonId: string,
    unitId: string,
    direction: "up" | "down"
  ) => {
    const lessons = unitLessons[unitId] ?? [];
    const idx = lessons.findIndex((l) => l.id === lessonId);
    if (idx < 0) return;
    if (direction === "up" && idx === 0) return;
    if (direction === "down" && idx === lessons.length - 1) return;

    const afterId =
      direction === "up"
        ? idx === 1
          ? null // move to start
          : lessons[idx - 2].id
        : lessons[idx + 1].id;

    try {
      await putJson(`${ADMIN}/lessons/reorder`, {
        item_id: lessonId,
        after_id: afterId,
      });
      await fetchLessons(unitId);
    } catch (e) {
      console.error("Failed to reorder lesson", e);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!curriculum) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Curriculum not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Title title={curriculum.title} />

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate("/curricula")}>
          <ArrowBackIcon />
        </IconButton>
        {editingTitle ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              size="small"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSaveTitle()}
            />
            <Button size="small" onClick={handleSaveTitle}>
              Save
            </Button>
            <Button size="small" onClick={() => setEditingTitle(false)}>
              Cancel
            </Button>
          </Box>
        ) : (
          <Typography
            variant="h5"
            sx={{ cursor: "pointer" }}
            onClick={() => setEditingTitle(true)}
          >
            {curriculum.title}
            <IconButton size="small" sx={{ ml: 1 }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Typography>
        )}
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2">Active</Typography>
          <Switch
            checked={curriculum.is_active}
            onChange={handleToggleActive}
          />
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {curriculum.description}
      </Typography>

      {/* Units Panel */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">Units ({units.length})</Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreateUnitOpen(true)}
          >
            Add Unit
          </Button>
        </Box>

        {units.map((unit, unitIdx) => (
          <Card key={unit.id} sx={{ mb: 1 }}>
            <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ display: "flex", flexDirection: "column", mr: 0.5 }}>
                  <IconButton
                    size="small"
                    disabled={unitIdx === 0}
                    onClick={() => handleMoveUnit(unit.id, "up")}
                    sx={{ p: 0.25 }}
                  >
                    <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={unitIdx === units.length - 1}
                    onClick={() => handleMoveUnit(unit.id, "down")}
                    sx={{ p: 0.25 }}
                  >
                    <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleToggleUnit(unit.id)}
                >
                  {expandedUnit === unit.id ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </IconButton>
                <Typography sx={{ flex: 1, ml: 1 }}>{unit.title}</Typography>
                <Chip
                  label={`${unit.lesson_count} lessons`}
                  size="small"
                  sx={{ mr: 1 }}
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleArchiveUnit(unit.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              <Collapse in={expandedUnit === unit.id}>
                <Divider sx={{ my: 1 }} />
                <List dense disablePadding>
                  {(unitLessons[unit.id] ?? []).map((lesson, lessonIdx) => (
                    <ListItem
                      key={lesson.id}
                      sx={{
                        opacity: lesson.status === "DISABLED" ? 0.5 : 1,
                      }}
                      secondaryAction={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Switch
                            size="small"
                            checked={lesson.status === "ACTIVE"}
                            onChange={() =>
                              handleToggleLessonStatus(lesson, unit.id)
                            }
                            title={
                              lesson.status === "ACTIVE"
                                ? "Disable lesson"
                                : "Enable lesson"
                            }
                          />
                          <IconButton
                            size="small"
                            disabled={lessonIdx === 0}
                            onClick={() =>
                              handleMoveLesson(lesson.id, unit.id, "up")
                            }
                          >
                            <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            disabled={
                              lessonIdx ===
                              (unitLessons[unit.id]?.length ?? 1) - 1
                            }
                            onClick={() =>
                              handleMoveLesson(lesson.id, unit.id, "down")
                            }
                          >
                            <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setCloneLessonId(lesson.id);
                              setCloneOpen(true);
                            }}
                          >
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              handleArchiveLesson(lesson.id, unit.id)
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={lesson.title}
                        secondary={
                          <Box
                            component="span"
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <Chip
                              label={lesson.type}
                              size="small"
                              variant="outlined"
                            />
                            {lesson.status === "DISABLED" && (
                              <Chip
                                label="Disabled"
                                size="small"
                                color="warning"
                              />
                            )}
                            <Typography variant="caption">
                              {lesson.node_count} questions | v{lesson.version}
                            </Typography>
                          </Box>
                        }
                        sx={{ cursor: "pointer" }}
                        onClick={() => navigate(`/lessons/${lesson.id}/edit`)}
                      />
                    </ListItem>
                  ))}
                  {unitLessons[unit.id]?.length === 0 && (
                    <ListItem>
                      <ListItemText
                        secondary="No lessons yet"
                        sx={{ fontStyle: "italic" }}
                      />
                    </ListItem>
                  )}
                </List>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setCreateLessonUnitId(unit.id);
                    setCreateLessonOpen(true);
                  }}
                  sx={{ mt: 1 }}
                >
                  Add Lesson
                </Button>
              </Collapse>
            </CardContent>
          </Card>
        ))}

        {units.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No units yet. Click "Add Unit" to get started.
          </Typography>
        )}
      </Box>

      {/* Feedback Groups Panel */}
      <FeedbackGroupsPanel
        curriculumId={curriculumId!}
        feedbackGroups={feedbackGroups}
        onRefresh={fetchCurriculum}
      />

      {/* Create Unit Dialog */}
      <Dialog
        open={createUnitOpen}
        onClose={() => setCreateUnitOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Unit</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="ID (slug)"
            fullWidth
            value={newUnitId}
            onChange={(e) =>
              setNewUnitId(e.target.value.toLowerCase().replace(SLUG_RE, ""))
            }
            helperText="Lowercase, hyphens only (e.g. unit-1)"
          />
          <TextField
            margin="dense"
            label="Title"
            fullWidth
            value={newUnitTitle}
            onChange={(e) => setNewUnitTitle(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateUnitOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateUnit}
            variant="contained"
            disabled={!newUnitId || !newUnitTitle}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Lesson Dialog */}
      <Dialog
        open={createLessonOpen}
        onClose={() => setCreateLessonOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Lesson</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            value={newLessonTitle}
            onChange={(e) => setNewLessonTitle(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Type"
            fullWidth
            select
            SelectProps={{ native: true }}
            value={newLessonType}
            onChange={(e) => setNewLessonType(e.target.value)}
          >
            <option value="STANDARD">Standard</option>
            <option value="REVIEW">Review</option>
            <option value="UNIT_QUIZ">Unit Quiz</option>
          </TextField>
          <Alert
            severity="info"
            variant="outlined"
            sx={{ mt: 1.5 }}
            icon={false}
          >
            <Typography
              variant="caption"
              component="div"
              sx={{ lineHeight: 1.8 }}
            >
              <strong>Standard</strong> — Regular teaching lesson with questions
              <br />
              <strong>Review</strong> — Re-asks questions the user got wrong
              earlier; auto-skipped if no mistakes
              <br />
              <strong>Unit Quiz</strong> — Graded assessment that completes the
              unit
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateLessonOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateLesson}
            variant="contained"
            disabled={!newLessonTitle}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone Lesson Dialog */}
      <Dialog
        open={cloneOpen}
        onClose={() => setCloneOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Clone Lesson</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Title Suffix"
            fullWidth
            value={cloneSuffix}
            onChange={(e) => setCloneSuffix(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneOpen(false)}>Cancel</Button>
          <Button onClick={handleCloneLesson} variant="contained">
            Clone
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
